import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import {
  Box, Container, Typography, Paper, Chip, Avatar,
  Divider, Button, CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useParams } from 'react-router-dom';
import { PROFILE_BLOCKS } from '../config/profileBlocks';
import { usePrintDocument } from '../hooks/usePrintDocument';
import CompactSheet from '../components/cv/CompactSheet';
import CompactControls from '../components/cv/CompactControls';
import { useCompactFit, useAutoFit } from '../hooks/useCompactFit';
import { DEFAULT_COMPACT_SETTINGS, buildCompactPerson, applyOverrides } from '../config/compactCV';
import { canEditProfile } from '../utils/permissions';
import { dedupeSoftware } from '../utils/softwareNames';


// Enskild profil skrivs ut på en sida i kompakt läge; anbudsvyn använder två.
const PROFILE_DEFAULTS = { ...DEFAULT_COMPACT_SETTINGS, pages: 1 };

// Valen låg tidigare i localStorage, gemensamt för alla profiler i webbläsaren.
// De läses in en gång som startvärde för den egna profilen och tas sedan bort.
const LEGACY_MODE_KEY = 'cvPreviewMode';
const LEGACY_SETTINGS_KEY = 'cvCompactSettings';

function readLegacy() {
  try {
    const raw = localStorage.getItem(LEGACY_SETTINGS_KEY);
    const mode = localStorage.getItem(LEGACY_MODE_KEY);
    if (!raw && !mode) return null;
    return { compactCV: raw ? JSON.parse(raw) : {}, layoutMode: mode || 'full' };
  } catch {
    return null;
  }
}

function clearLegacy() {
  try {
    localStorage.removeItem(LEGACY_SETTINGS_KEY);
    localStorage.removeItem(LEGACY_MODE_KEY);
  } catch { /* privat läge e.d. – inget att städa */ }
}

const saveSettings = (uid, state) => setDoc(
  doc(db, 'profiles', uid, 'settings', 'compactCV'), { ...state, updatedAt: new Date() },
);

/** Det som sparas – autoNote är en tillfällig förklaring, inte ett val. */
const savedState = (settings, mode) => {
  const { autoNote, ...clean } = settings;
  return { compactCV: clean, layoutMode: mode };
};

export default function CVPreview() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const { userId } = useParams();
  const targetUserId = userId || currentUser.uid;
  const canEdit = !userId || canEditProfile(userProfile, currentUser.uid, userId);
  const [profile, setProfile] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  // Valen för kompakt läge sparas på profilen, så att alla som öppnar den
  // ser och skriver ut samma dokument.
  const [mode, setMode] = useState('full');
  const [settings, setSettings] = useState(PROFILE_DEFAULTS);
  const savedRef = useRef(null);         // senast sparade värde, som JSON
  const userChangedRef = useRef(false);  // användaren har ändrat något sedan senaste sparning
  const readFailedRef = useRef(false);   // valen gick inte att läsa

  const fullRef = useRef(null);
  const compactRef = useRef(null);
  const printRef = mode === 'compact' ? compactRef : fullRef;
  const handlePrint = usePrintDocument(printRef, {
    title: profile?.personal?.name || 'Konsultprofil',
    margin: mode === 'compact' ? '8mm' : '10mm',
    compressImages: true,
  });

  // Ändringar från kontrollpanelen – de enda som får leda till en sparning.
  const changeSettings = useCallback(s => { userChangedRef.current = true; setSettings(s); }, []);
  const changeMode = useCallback(m => { userChangedRef.current = true; setMode(m); }, []);

  /* Sparar valen på profilen, samma mönster som cvVariants.compactCV, men bara
     efter en ändring som användaren själv gjort. Det auto-anpassningen tar bort
     gäller i vyn tills användaren ändrar något – då sparas det som syns. Den
     som bara får läsa profilen kan ändra valen i vyn, men de sparas inte. */
  useEffect(() => {
    if (loading || !canEdit || readFailedRef.current || !userChangedRef.current) return undefined;
    const state = savedState(settings, mode);
    const json = JSON.stringify(state);
    if (json === savedRef.current) { userChangedRef.current = false; return undefined; }
    const t = setTimeout(() => {
      userChangedRef.current = false;
      saveSettings(targetUserId, state)
        .then(() => { savedRef.current = json; })
        .catch(() => { userChangedRef.current = true; });
    }, 900);
    return () => clearTimeout(t);
  }, [settings, mode, loading, canEdit, targetUserId]);

  // Utfällda uppdrag – lokalt, den här vyn är en förhandsgranskning.
  const [expanded, setExpanded] = useState([]);
  const toggleExpand = useCallback((userId, index) => {
    setExpanded(prev => prev.includes(index)
      ? prev.filter(i => i !== index)
      : [...prev, index]);
  }, []);

  const persons = useMemo(() => (
    profile
      ? [applyOverrides(buildCompactPerson({ userId: targetUserId, profile }), { expanded })]
      : []
  ), [profile, targetUserId, expanded]);

  const fit = useCompactFit(compactRef, settings, [persons, mode]);
  const setSettingsCb = useCallback(s => setSettings(s), []);
  useAutoFit(mode === 'compact' ? fit : { ...fit, overflowRows: 0 }, settings, setSettingsCb);

  useEffect(() => {
    async function load() {
      const [profileSnap, settingsSnap] = await Promise.all([
        getDoc(doc(db, 'profiles', targetUserId)),
        // Går valen inte att läsa visas profilen ändå, med standardvalen.
        getDoc(doc(db, 'profiles', targetUserId, 'settings', 'compactCV')).catch(() => null),
      ]);
      if (profileSnap.exists()) setProfile(profileSnap.data());

      const hasSaved = Boolean(settingsSnap?.exists());
      const legacy = settingsSnap && !hasSaved && targetUserId === currentUser.uid
        ? readLegacy() : null;
      const stored = hasSaved ? settingsSnap.data() : legacy;
      const nextSettings = { ...PROFILE_DEFAULTS, ...(stored?.compactCV || {}) };
      const nextMode = stored?.layoutMode || 'full';
      setSettings(nextSettings);
      setMode(nextMode);
      // Gick valen inte att läsa sparas inget, så att de sparade inte skrivs över.
      userChangedRef.current = false;
      readFailedRef.current = !settingsSnap;
      const loaded = savedState(nextSettings, nextMode);
      savedRef.current = JSON.stringify(loaded);
      // Valen från localStorage sparas direkt, som de lästes – innan
      // auto-anpassningen hunnit ändra något.
      if (legacy) {
        saveSettings(targetUserId, loaded).then(clearLegacy).catch(() => { savedRef.current = null; });
      }

      const userSnap = await getDoc(doc(db, 'users', targetUserId));
      if (userSnap.exists() && userSnap.data().companyId) {
        const compSnap = await getDoc(doc(db, 'companies', userSnap.data().companyId));
        if (compSnap.exists()) setCompany(compSnap.data());
      }
      setLoading(false);
    }
    load();
  }, [targetUserId, currentUser.uid]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );
  if (!profile) return (
    <Box sx={{ p: 4 }}>
      <Typography>Ingen profil hittad.</Typography>
    </Box>
  );

  const blockOrder = profile.blockOrder || [];

  return (
    <Box>
      {/* Header */}
      <Box className="no-print" sx={{ backgroundColor: '#1a1a1a', color: 'white', py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button color="inherit" startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}>
              Tillbaka
            </Button>
            <Typography variant="h5" fontWeight="bold">Konsultprofil</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canEdit && (
              <Button variant="outlined" startIcon={<EditIcon />}
                onClick={() => navigate(userId && userId !== currentUser.uid ? `/profile/${userId}` : '/profile')}
                sx={{ borderColor: 'rgba(255,255,255,0.5)', color: 'white',
                  '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                Redigera profil
              </Button>
            )}
            <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}
              sx={{ bgcolor: 'white', color: '#1a1a1a' }}>
              Skriv ut / Spara PDF
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Bakgrund följer tema */}
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}
        className="cv-background">
        <Container maxWidth="md">

          <CompactControls
            mode={mode}
            onModeChange={changeMode}
            settings={settings}
            onSettingsChange={changeSettings}
            persons={persons}
            fit={fit}
          />

          {mode === 'compact' ? (
            <CompactSheet
              docRef={compactRef}
              persons={persons}
              settings={settings}
              company={company}
              docTitle="Konsultprofil"
              onToggleExpand={toggleExpand}
            />
          ) : (
          /* Dokumentet alltid vitt */
          <Paper elevation={3} id="cv-content" ref={fullRef}
            sx={{ p: 5, borderRadius: 2, bgcolor: '#ffffff', color: '#111' }}>

            {/* SIDHUVUD */}
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              mb: 4, pb: 2, borderBottom: '3px solid #1a1a1a'
            }}>
              <Box>
                {company?.logo ? (
                  <Box component="img" src={company.logo}
                    sx={{ height: 50, objectFit: 'contain' }} />
                ) : (
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#1a1a1a' }}>
                    {company?.name || ''}
                  </Typography>
                )}
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="overline" sx={{ color: '#888', letterSpacing: 3 }}>
                  KONSULTPROFIL
                </Typography>
                <Typography variant="caption" sx={{ color: '#888' }} display="block">
                  {new Date().toLocaleDateString('sv-SE', { year: 'numeric', month: 'long' })}
                </Typography>
              </Box>
            </Box>

            {/* PERSONUPPGIFTER */}
            <Box sx={{ display: 'flex', gap: 3, mb: 4, alignItems: 'flex-start' }}>
              <Avatar src={profile.personal?.photo}
                sx={{ width: 100, height: 100, fontSize: 40, flexShrink: 0 }}>
                {!profile.personal?.photo && (profile.personal?.name?.charAt(0) || '?')}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#111' }}>
                  {profile.personal?.name}
                </Typography>
                <Typography variant="h6" sx={{ color: '#444' }} gutterBottom>
                  {profile.personal?.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {profile.personal?.location && (
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      📍 {profile.personal.location}
                    </Typography>
                  )}
                  {profile.personal?.email && (
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      ✉ {profile.personal.email}
                    </Typography>
                  )}
                  {profile.personal?.phone && (
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      ☎ {profile.personal.phone}
                    </Typography>
                  )}
                  {profile.personal?.linkedin && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="#0A66C2">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      <Typography variant="body2" sx={{ color: '#333' }}>
                        {profile.personal.linkedin}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 3, borderColor: '#ddd' }} />

            {/* INGRESS */}
            {profile.ingress && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="body1"
                  sx={{ lineHeight: 1.8, fontStyle: 'italic', color: '#222' }}>
                  {profile.ingress}
                </Typography>
              </Box>
            )}

            {/* DYNAMISKA BLOCK */}
            {blockOrder
              .filter(id => !['personal', 'ingress'].includes(id))
              .filter(id => profile.visibleBlocks?.includes(id))
              .map(id => {
                const block = PROFILE_BLOCKS.find(b => b.id === id);
                if (!block) return null;

                if (id === 'competencies' && profile.competencies?.length > 0) return (
                  <CVSection key={id} title={block.label}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {profile.competencies.map((c, i) => (
                        <Chip key={i} label={c} size="small" variant="outlined"
                          sx={{ color: '#222', borderColor: '#999', bgcolor: 'transparent' }} />
                      ))}
                    </Box>
                  </CVSection>
                );

                if (id === 'personal_profile' && profile.personal_profile?.length > 0) return (
                  <CVSection key={id} title={block.label}>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {profile.personal_profile.map((p, i) => (
                        <Typography key={i} component="li" variant="body2"
                          sx={{ color: '#222', mb: 0.5 }}>{p}</Typography>
                      ))}
                    </Box>
                  </CVSection>
                );

                if (id === 'industries' && profile.industries?.length > 0) return (
                  <CVSection key={id} title={block.label}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {profile.industries.map((ind, i) => (
                        <Chip key={i} label={ind} size="small" variant="outlined"
                          sx={{ color: '#222', borderColor: '#999', bgcolor: 'transparent' }} />
                      ))}
                    </Box>
                  </CVSection>
                );

                if (id === 'assignments' && profile.assignments?.length > 0) return (
                  <CVSection key={id} title={block.label}>
                    {profile.assignments.map((a, i) => (
                      <Box key={i} className="cv-assignment" sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                          {a.image && (
                            <Box component="img" src={a.image} sx={{
                              width: '22%', flexShrink: 0,
                              aspectRatio: '4/3', objectFit: 'cover', borderRadius: 1
                            }} />
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{
                              display: 'flex', justifyContent: 'space-between',
                              alignItems: 'flex-start'
                            }}>
                              <Typography fontWeight="bold" sx={{ color: '#111' }}>
                                {a.name}
                              </Typography>
                              {a.budget && (
                                <Chip label={a.budget} size="small" variant="outlined"
                                  sx={{ color: '#333', borderColor: '#999' }} />
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: '#555' }}>
                              {[a.period, a.client, a.role].filter(Boolean).join(' | ')}
                            </Typography>
                            {a.projectDescription && (
                              <Typography variant="body2" sx={{ color: '#444', mt: 1 }}>
                                {a.projectDescription}
                              </Typography>
                            )}
                            {a.personalDescription && (
                              <Box sx={{ mt: 1, pl: 1.5, borderLeft: '3px solid #333' }}>
                                <Typography variant="caption" fontWeight="bold"
                                  sx={{ color: '#222' }}>
                                  Roll och insats
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#333', mt: 0.5 }}>
                                  {a.personalDescription}
                                </Typography>
                              </Box>
                            )}
                            {!a.projectDescription && !a.personalDescription && a.description && (
                              <Typography variant="body2" sx={{ color: '#444', mt: 1 }}>
                                {a.description}
                              </Typography>
                            )}
                            {dedupeSoftware(a.software).length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                {dedupeSoftware(a.software).map((s, si) => (
                                  <Chip key={si} label={s} size="small"
                                    sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.7rem' }} />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                        {i < profile.assignments.length - 1 && (
                          <Divider sx={{ mt: 2, borderColor: '#eee' }} />
                        )}
                      </Box>
                    ))}
                  </CVSection>
                );

                if (id === 'employment' && profile.employment?.length > 0) return (
                  <CVSection key={id} title={block.label}>
                    {profile.employment.map((e, i) => (
                      <Box key={i} className="cv-assignment" sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography fontWeight="bold" sx={{ color: '#111' }}>
                            {e.company}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#555' }}>
                            {e.period}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#444' }}>{e.title}</Typography>
                        {e.description && (
                          <Typography variant="body2" sx={{ color: '#444', mt: 0.5 }}>
                            {e.description}
                          </Typography>
                        )}
                        {i < profile.employment.length - 1 && (
                          <Divider sx={{ mt: 1.5, borderColor: '#eee' }} />
                        )}
                      </Box>
                    ))}
                  </CVSection>
                );

                if (id === 'education' && profile.education?.length > 0) return (
                  <CVSection key={id} title={block.label}>
                    {profile.education.map((e, i) => (
                      <Box key={i} className="cv-assignment" sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography fontWeight="bold" sx={{ color: '#111' }}>
                            {e.degree}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#555' }}>
                            {e.period}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#444' }}>{e.school}</Typography>
                        {e.description && (
                          <Typography variant="body2" sx={{ color: '#444', mt: 0.5 }}>
                            {e.description}
                          </Typography>
                        )}
                        {i < profile.education.length - 1 && (
                          <Divider sx={{ mt: 1.5, borderColor: '#eee' }} />
                        )}
                      </Box>
                    ))}
                  </CVSection>
                );

                if (id === 'certifications' && profile.certifications?.length > 0) return (
                  <CVSection key={id} title={block.label}>
                    {profile.certifications.map((c, i) => (
                      <Box key={i} className="cv-assignment"
                        sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#222' }}>{c.name}</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}>
                          {c.issuer} {c.year}
                        </Typography>
                      </Box>
                    ))}
                  </CVSection>
                );

                if (id === 'tools' && profile.tools) return (
                  <CVSection key={id} title={block.label}>
                    {profile.tools.expert?.length > 0 && (
                      <Box className="cv-assignment" sx={{ mb: 1.5 }}>
                        <Typography variant="caption" fontWeight="bold" sx={{ color: '#333' }}>
                          Mycket goda kunskaper
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {[...profile.tools.expert].sort((a, b) => a.localeCompare(b, 'sv')).map((t, i) => (
                            <Chip key={i} label={t} size="small" variant="outlined"
                              sx={{ color: '#111', borderColor: '#333',
                                fontWeight: 'bold', bgcolor: 'transparent' }} />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {profile.tools.good?.length > 0 && (
                      <Box className="cv-assignment" sx={{ mb: 1.5 }}>
                        <Typography variant="caption" fontWeight="bold" sx={{ color: '#333' }}>
                          Goda kunskaper
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {[...profile.tools.good].sort((a, b) => a.localeCompare(b, 'sv')).map((t, i) => (
                            <Chip key={i} label={t} size="small" variant="outlined"
                              sx={{ color: '#444', borderColor: '#666', bgcolor: 'transparent' }} />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {profile.tools.basic?.length > 0 && (
                      <Box className="cv-assignment">
                        <Typography variant="caption" fontWeight="bold" sx={{ color: '#333' }}>
                          Grundlaggande kunskaper
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {[...profile.tools.basic].sort((a, b) => a.localeCompare(b, 'sv')).map((t, i) => (
                            <Chip key={i} label={t} size="small" variant="outlined"
                              sx={{ color: '#666', borderColor: '#999', bgcolor: 'transparent' }} />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </CVSection>
                );

                if (id === 'standards' && profile.standards?.length > 0) return (
                  <CVSection key={id} title={block.label}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {profile.standards.map((s, i) => (
                        <Chip key={i} label={s} size="small" variant="outlined"
                          sx={{ color: '#222', borderColor: '#888', bgcolor: 'transparent' }} />
                      ))}
                    </Box>
                  </CVSection>
                );

                if (id === 'languages' && profile.languages?.length > 0) return (
                  <CVSection key={id} title={block.label}>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      {profile.languages.map((l, i) => (
                        <Box key={i} className="cv-assignment">
                          <Typography variant="body2" fontWeight="bold" sx={{ color: '#111' }}>
                            {l.language}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#555' }}>
                            {l.level}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CVSection>
                );

                if (id === 'project_scale' && profile.project_scale) return (
                  <CVSection key={id} title={block.label}>
                    <Typography variant="body2" sx={{ color: '#222' }}>
                      {profile.project_scale}
                    </Typography>
                  </CVSection>
                );

                if (id === 'publications' && profile.publications?.length > 0) return (
                  <CVSection key={id} title={block.label}>
                    {profile.publications.map((p, i) => (
                      <Box key={i} className="cv-assignment" sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: '#111' }}>
                          {p.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}>
                          {p.venue} {p.year}
                        </Typography>
                      </Box>
                    ))}
                  </CVSection>
                );

                if (id === 'licenses' && profile.licenses) return (
                  <CVSection key={id} title={block.label}>
                    <Typography variant="body2" sx={{ color: '#222' }}>
                      {profile.licenses}
                    </Typography>
                  </CVSection>
                );

                if (id === 'board' && profile.board?.length > 0) return (
                  <CVSection key={id} title={block.label}>
                    {profile.board.map((b, i) => (
                      <Box key={i} className="cv-assignment"
                        sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#222' }}>
                          {b.role} – {b.org}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}>
                          {b.period}
                        </Typography>
                      </Box>
                    ))}
                  </CVSection>
                );

                return null;
              })}

            {/* SIDFOT */}
            <Box sx={{
              mt: 4, pt: 2, borderTop: '1px solid #e0e0e0',
              display: 'flex', justifyContent: 'space-between'
            }}>
              <Typography variant="caption" sx={{ color: '#555' }}>
                {company?.name || ''}
              </Typography>
              <Typography variant="caption" sx={{ color: '#555' }}>
                {company?.website || ''}
              </Typography>
              <Typography variant="caption" sx={{ color: '#555' }}>
                {company?.email || ''}
              </Typography>
            </Box>

          </Paper>
          )}
        </Container>
      </Box>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .cv-background {
            background: white !important;
            padding: 0 !important;
          }
          body {
            margin: 0 !important;
            padding: 0 !important;
          }
          @page {
            size: A4 portrait;
            margin: 15mm 15mm 15mm 15mm;
          }
          #cv-content {
            width: 210mm !important;
            max-width: 210mm !important;
            min-height: 297mm !important;
            margin: 0 auto !important;
            padding: 10mm !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          .MuiContainer-root {
            max-width: 100% !important;
            padding: 0 !important;
          }
          .MuiPaper-root {
            box-shadow: none !important;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          .cv-section {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .cv-section-title {
            break-after: avoid;
            page-break-after: avoid;
          }
          .cv-assignment {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          hr, .MuiDivider-root {
            break-after: avoid;
            page-break-after: avoid;
          }
          img {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .MuiChip-root {
            border: 1px solid #999 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .MuiChip-outlined {
            background-color: transparent !important;
            border: 1px solid #888 !important;
            color: #222 !important;
          }
        }
      `}</style>
    </Box>
  );
}

function CVSection({ title, children }) {
  return (
    <Box className="cv-section" sx={{ mb: 4, breakInside: 'avoid' }}>
      <Typography className="cv-section-title" variant="h6" fontWeight="bold"
        sx={{ color: '#111' }} gutterBottom>
        {title}
      </Typography>
      <Divider sx={{ mb: 2, borderColor: '#ddd' }} />
      {children}
    </Box>
  );
}